import { VStack } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { CpvNode } from "./cpvHierarchy";
import { flattenVisible, parentOf } from "./cpvHierarchy";
import { CpvTreeNode } from "./CpvTreeNode";
import { useCpvTreeKeyboard } from "./useCpvTreeKeyboard";

type Props = {
  nodes: CpvNode[];
  selected: Set<string>;
  expandedIds: Set<string>;
  onToggleSelect: (code: string) => void;
  onToggleExpand: (code: string) => void;
  onlyAdditional?: (code: string) => boolean;
  /** Where focus goes when the reader presses Escape; without it Escape is left to the page. */
  onEscape?: () => void;
};

export const CpvCategoryTree = ({
  nodes,
  selected,
  expandedIds,
  onToggleSelect,
  onToggleExpand,
  onlyAdditional,
  onEscape,
}: Props) => {
  const { t } = useTranslation();
  const ancestorsOfSelected = useMemo(() => collectAncestors(selected), [selected]);
  const rows = useMemo(() => flattenVisible(nodes, expandedIds), [nodes, expandedIds]);

  const handlers = useRef({ onToggleSelect, onToggleExpand });
  useEffect(() => {
    handlers.current = { onToggleSelect, onToggleExpand };
  });
  const toggleSelect = useCallback((code: string) => handlers.current.onToggleSelect(code), []);
  const toggleExpand = useCallback((code: string) => handlers.current.onToggleExpand(code), []);

  const { containerRef, treeHandlers } = useCpvTreeKeyboard({
    rows,
    expandedIds,
    onToggleSelect: toggleSelect,
    onToggleExpand: toggleExpand,
    onEscape,
  });

  return (
    <VStack
      ref={containerRef}
      role="tree"
      aria-label={t("Categories") ?? undefined}
      aria-multiselectable
      align="stretch"
      spacing="0"
      {...treeHandlers}
    >
      {rows.map(({ node, posInSet, setSize }) => (
        <CpvTreeNode
          key={node.code}
          posInSet={posInSet}
          setSize={setSize}
          node={node}
          isSelected={selected.has(node.code)}
          isExpanded={expandedIds.has(node.code)}
          hasSelectedDescendant={ancestorsOfSelected.has(node.code)}
          hasChildren={node.children.length > 0}
          isOnlyAdditional={onlyAdditional?.(node.code) ?? false}
          onToggleSelect={toggleSelect}
          onToggleExpand={toggleExpand}
        />
      ))}
    </VStack>
  );
};

function collectAncestors(selected: Set<string>): Set<string> {
  const out = new Set<string>();
  for (const code of selected) {
    for (let parent = safeParentOf(code); parent !== null; parent = safeParentOf(parent)) {
      if (out.has(parent)) break;
      out.add(parent);
    }
  }
  return out;
}

function safeParentOf(code: string): string | null {
  try {
    return parentOf(code);
  } catch {
    return null;
  }
}
